import {
  createWorkspaceAgent,
  deleteWorkspaceAgent,
  listProjectSummaries,
  listWorkspaceAgents,
  listWorkspaceAgentsForCompany,
  updateWorkspaceAgent,
  updateWorkspaceAgentAssignments,
  updateWorkspaceAgentFocus,
  type CoworkAgentAssignment,
  type CoworkWorkspaceAgent,
} from "@/cowork/api"
import { useCoworkWorkspace } from "@/cowork/workspace-context"
import { useCollectionScope } from "@/cowork/scope-url"
import { useLanguage } from "@/context/language"
import { SettingsReturnLink } from "@/components/settings-return-link"
import { coworkAgentTemplates, hasWorkspaceAgentTemplate, workspaceAgentFromTemplate } from "@/cowork/agent-templates"
import { useSettingsNavigate } from "@/cowork/settings-navigation"
import { createMemo, createResource, For, Show } from "solid-js"
import { createStore } from "solid-js/store"

type ScopeType = "workspace" | "entity" | "project"

export function CoworkAgentsPage() {
  const language = useLanguage()
  const context = useCoworkWorkspace()
  const scope = useCollectionScope()
  const navigate = useSettingsNavigate()
  const [agents, agentActions] = createResource(
    () => (scope().companyID && context.state.entityId !== scope().companyID) ||
      (scope().projectID && context.selectedProject()?.id !== scope().projectID)
        ? undefined : `${scope().companyID ?? ""}:${scope().projectID ?? ""}`,
    () => listWorkspaceAgentsForCompany(scope().companyID),
  )
  const [projects] = createResource(listProjectSummaries)
  const [state, setState] = createStore({
    editing: "",
    scopeType: "workspace" as ScopeType,
    entityIDs: [] as string[],
    projectIDs: [] as string[],
    preferredSkills: [] as string[],
    instructions: "",
    saving: "",
    error: "",
  })

  const visibleAgents = createMemo(() => {
    const entityID = scope().companyID
    const projectID = scope().projectID
    return (agents() ?? []).filter((agent) => {
      if (agent.assignments.length === 0) return true
      if (!projectID && !entityID) return true
      return agent.assignments.some(
        (assignment) =>
          assignment.scope_type === "workspace" ||
          (assignment.scope_type === "entity" && assignment.scope_id === entityID) ||
          (assignment.scope_type === "project" && assignment.scope_id === projectID),
      )
    })
  })

  const editScope = (agent: CoworkWorkspaceAgent) => {
    const workspace = agent.assignments.some((item) => item.scope_type === "workspace")
    const entityIDs = agent.assignments
      .filter((item) => item.scope_type === "entity")
      .flatMap((item) => item.scope_id ?? [])
    const projectIDs = agent.assignments
      .filter((item) => item.scope_type === "project")
      .flatMap((item) => item.scope_id ?? [])
    setState({
      editing: agent.id,
      scopeType: workspace ? "workspace" : entityIDs.length > 0 ? "entity" : "project",
      entityIDs,
      projectIDs,
      preferredSkills: Array.isArray(agent.metadata.preferred_skills)
        ? agent.metadata.preferred_skills.filter((skill): skill is string => typeof skill === "string") : [],
      instructions: agent.instructions || "",
      error: "",
    })
  }

  const saveScope = async (agentID: string) => {
    const assignments = scopeAssignments(state.scopeType, state.entityIDs, state.projectIDs)
    setState({ saving: agentID, error: "" })
    try {
      await updateWorkspaceAgentAssignments(agentID, assignments)
      await updateWorkspaceAgentFocus(agentID, state.preferredSkills, state.instructions)
      await agentActions.refetch()
      setState("editing", "")
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("saving", "")
    }
  }

  const toggleAgent = async (agent: CoworkWorkspaceAgent) => {
    setState({ saving: agent.id, error: "" })
    try {
      await updateWorkspaceAgent(agent.id, { status: agent.status === "active" ? "disabled" : "active" })
      await agentActions.refetch()
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("saving", "")
    }
  }

  const removeAgent = async (agent: CoworkWorkspaceAgent) => {
    setState({ saving: agent.id, error: "" })
    try {
      await deleteWorkspaceAgent(agent.id)
      await agentActions.refetch()
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("saving", "")
    }
  }

  return (
    <div class="min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10">
      <div class="mx-auto max-w-[1300px]">
        <SettingsReturnLink />
        <header class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 class="text-[30px] font-semibold tracking-[-0.025em]">
              {language.t("cowork.enterprise.nav.myAgents")}
            </h1>
            <p class="mt-1 text-[13px] text-v2-text-text-muted">
              {language.t("cowork.enterprise.agents.workspaceDescription")}
            </p>
          </div>
          <button
            type="button"
            class="rounded-lg bg-v2-background-bg-contrast px-4 py-2.5 text-[12px] font-semibold text-v2-text-text-contrast"
            onClick={() => navigate("/agents/library")}
          >
            {language.t("cowork.enterprise.agents.browseLibrary")}
          </button>
        </header>

        <Show when={state.error}>
          <div class="mt-5 rounded-lg bg-v2-state-bg-danger px-4 py-3 text-[12px] text-v2-state-fg-danger">
            {state.error}
          </div>
        </Show>

        <Show
          when={!agents.loading && visibleAgents().length > 0}
          fallback={
            <div class="mt-7 rounded-xl border border-v2-border-border-base bg-v2-background-bg-base px-6 py-14 text-center">
              <Show
                when={!agents.loading}
                fallback={
                  <span class="size-5 animate-spin rounded-full border-2 border-v2-border-border-base border-t-v2-icon-icon-base" />
                }
              >
                <h2 class="text-[15px] font-semibold">{language.t("cowork.enterprise.agents.empty")}</h2>
                <p class="mx-auto mt-1 max-w-md text-[12px] text-v2-text-text-muted">
                  {language.t("cowork.enterprise.agents.emptyDescription")}
                </p>
              </Show>
            </div>
          }
        >
          <div class="mt-7 grid gap-4 lg:grid-cols-2">
            <For each={visibleAgents()}>
              {(agent) => (
                <article class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-5">
                  <div class="flex items-start justify-between gap-4">
                    <div class="min-w-0">
                      <div class="flex flex-wrap items-center gap-2">
                        <h2 class="truncate text-[16px] font-semibold">{agent.name}</h2>
                        <span
                          class="rounded-md px-2 py-1 text-[10px] font-medium"
                          classList={{
                            "bg-v2-state-bg-success text-v2-state-fg-success": agent.status === "active",
                            "bg-v2-background-bg-layer-03 text-v2-text-text-muted": agent.status !== "active",
                          }}
                        >
                          {language.t(
                            agent.status === "active" ? "cowork.enterprise.status.active" : "cowork.agents.disabled",
                          )}
                        </span>
                      </div>
                      <p class="mt-2 line-clamp-2 text-[12px] leading-5 text-v2-text-text-muted">
                        {agent.purpose || language.t("cowork.enterprise.agents.noPurpose")}
                      </p>
                    </div>
                    <div class="grid size-10 shrink-0 place-items-center rounded-lg bg-v2-background-bg-layer-03 text-[12px] font-semibold">
                      {agent.name.charAt(0).toUpperCase()}
                    </div>
                  </div>

                  <div class="mt-4">
                    <div class="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-v2-text-text-faint">
                      {language.t("cowork.enterprise.dashboard.scope")}
                    </div>
                    <div class="flex flex-wrap gap-1.5">
                      <Show
                        when={agent.assignments.length > 0}
                        fallback={
                          <span class="rounded-md bg-v2-state-bg-warning px-2 py-1 text-[10px] text-v2-state-fg-warning">
                            {language.t("cowork.enterprise.agents.notAssigned")}
                          </span>
                        }
                      >
                        <For each={agent.assignments}>
                          {(assignment) => (
                            <span class="rounded-md bg-v2-background-bg-layer-03 px-2 py-1 text-[10px] font-medium">
                              {assignment.scope_name || scopeFallback(assignment, language)}
                            </span>
                          )}
                        </For>
                      </Show>
                    </div>
                  </div>

                  <Show when={state.editing === agent.id}>
                    <div class="mt-4 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4">
                      <div class="flex flex-wrap gap-4">
                        <For each={["workspace", "entity", "project"] as ScopeType[]}>
                          {(scope) => (
                            <label class="flex items-center gap-2 text-[12px]">
                              <input
                                type="radio"
                                name={`scope-${agent.id}`}
                                checked={state.scopeType === scope}
                                onChange={() => setState("scopeType", scope)}
                              />
                              {language.t(
                                `cowork.enterprise.agents.scope.${scope}` as keyof typeof import("@/i18n/en").dict,
                              )}
                            </label>
                          )}
                        </For>
                      </div>
                      <Show when={state.scopeType === "entity"}>
                        <div class="mt-4 grid gap-2 sm:grid-cols-2">
                          <For each={context.companies() ?? []}>
                            {(company) => (
                              <ScopeCheckbox
                                label={company.name}
                                checked={state.entityIDs.includes(company.id)}
                                onChange={(checked) =>
                                  setState("entityIDs", toggleID(state.entityIDs, company.id, checked))
                                }
                              />
                            )}
                          </For>
                        </div>
                      </Show>
                      <Show when={state.scopeType === "project"}>
                        <div class="mt-4 grid gap-2 sm:grid-cols-2">
                          <For each={projects() ?? []}>
                            {(project) => (
                              <ScopeCheckbox
                                label={`${project.name} · ${project.company_name}`}
                                checked={state.projectIDs.includes(project.id)}
                                onChange={(checked) =>
                                  setState("projectIDs", toggleID(state.projectIDs, project.id, checked))
                                }
                              />
                            )}
                          </For>
                        </div>
                      </Show>
                      <label class="mt-4 block text-[12px] font-semibold">
                        {language.t("cowork.enterprise.agents.instructions")}
                        <textarea rows={3} maxLength={5000} value={state.instructions}
                          onInput={(event) => setState("instructions", event.currentTarget.value)}
                          class="mt-2 w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-base p-3 text-[12px] font-normal" />
                      </label>
                      <p class="mt-1 text-[11px] text-v2-text-text-muted">{language.t("cowork.enterprise.agents.instructionsDescription")}</p>
                      <details class="mt-4">
                        <summary class="cursor-pointer text-[12px] font-semibold">{language.t("cowork.enterprise.agents.focusSkills")}</summary>
                        <p class="mt-2 text-[11px] text-v2-text-text-muted">{language.t("cowork.enterprise.agents.focusDescription")}</p>
                        <div class="mt-2 grid max-h-48 gap-2 overflow-y-auto sm:grid-cols-2">
                          <For each={Array.isArray(agent.metadata.allowed_skills) ? agent.metadata.allowed_skills.filter((skill): skill is string => typeof skill === "string") : []}>
                            {(skill) => <ScopeCheckbox label={skill} checked={state.preferredSkills.includes(skill)}
                              onChange={(checked) => setState("preferredSkills", toggleID(state.preferredSkills, skill, checked))} />}
                          </For>
                        </div>
                      </details>
                      <div class="mt-4 flex justify-end gap-2">
                        <button
                          type="button"
                          class="rounded-md px-3 py-2 text-[11px]"
                          onClick={() => setState("editing", "")}
                        >
                          {language.t("common.cancel")}
                        </button>
                        <button
                          type="button"
                          class="rounded-md bg-v2-background-bg-contrast px-3 py-2 text-[11px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
                          disabled={state.saving === agent.id}
                          onClick={() => void saveScope(agent.id)}
                        >
                          {language.t("common.save")}
                        </button>
                      </div>
                    </div>
                  </Show>

                  <div class="mt-5 flex flex-wrap justify-end gap-2 border-t border-v2-border-border-muted pt-4">
                    <button
                      type="button"
                      class="rounded-md border border-v2-border-border-base px-3 py-2 text-[11px] disabled:cursor-not-allowed disabled:opacity-50"
                      disabled
                      onClick={() => editScope(agent)}
                    >
                      {language.t("cowork.enterprise.agents.editScope")}
                    </button>
                    <button
                      type="button"
                      class="rounded-md border border-v2-border-border-base px-3 py-2 text-[11px] disabled:cursor-not-allowed disabled:opacity-50"
                      disabled
                      onClick={() => void toggleAgent(agent)}
                    >
                      {language.t(agent.status === "active" ? "cowork.agents.disable" : "cowork.agents.enable")}
                    </button>
                    <button
                      type="button"
                      class="rounded-md border border-v2-border-border-base px-3 py-2 text-[11px] text-v2-state-fg-danger disabled:cursor-not-allowed disabled:opacity-50"
                      disabled
                      onClick={() => void removeAgent(agent)}
                    >
                      {language.t("common.delete")}
                    </button>
                  </div>
                </article>
              )}
            </For>
          </div>
        </Show>
      </div>
    </div>
  )
}

export function CoworkAgentLibraryPage() {
  const language = useLanguage()
  const navigate = useSettingsNavigate()
  const [agents] = createResource(listWorkspaceAgents)
  const [state, setState] = createStore({ adding: "", added: "", error: "" })
  const isAdded = (templateID: string) =>
    state.added === templateID || hasWorkspaceAgentTemplate(agents() ?? [], templateID)

  const addTemplate = async (template: (typeof coworkAgentTemplates)[number]) => {
    if (state.adding || agents.loading || isAdded(template.id)) return
    setState({ adding: template.id, error: "" })
    try {
      const templateAgent = workspaceAgentFromTemplate(template, language)
      await createWorkspaceAgent({
        name: templateAgent.name,
        purpose: templateAgent.purpose,
        mode: templateAgent.mode,
        status: "active",
        template_id: templateAgent.template_id,
        tools: templateAgent.tools,
        integrations: templateAgent.integrations,
        permissions: templateAgent.permissions,
        assignments: [{ scope_type: "workspace" }],
      })
      setState("added", template.id)
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("adding", "")
    }
  }

  return (
    <div class="min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10">
      <div class="mx-auto max-w-[1200px]">
        <SettingsReturnLink />
        <header>
          <h1 class="text-[30px] font-semibold tracking-[-0.025em]">
            {language.t("cowork.enterprise.nav.agentLibrary")}
          </h1>
          <p class="mt-1 text-[13px] text-v2-text-text-muted">
            {language.t("cowork.enterprise.agents.workspaceLibraryDescription")}
          </p>
        </header>

        <Show when={state.error}>
          <div class="mt-5 rounded-lg bg-v2-state-bg-danger px-4 py-3 text-[12px] text-v2-state-fg-danger">
            {state.error}
          </div>
        </Show>

        <div class="mt-7 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <For each={coworkAgentTemplates}>
            {(template) => (
              <article class="flex min-h-64 flex-col rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-5">
                <div class="grid size-10 place-items-center rounded-lg bg-v2-background-bg-layer-03 text-[12px] font-semibold">
                  {language.t(template.nameKey).charAt(0)}
                </div>
                <h2 class="mt-4 text-[15px] font-semibold">{language.t(template.nameKey)}</h2>
                <p class="mt-2 text-[12px] leading-5 text-v2-text-text-muted">{language.t(template.descriptionKey)}</p>
                <div class="mt-auto flex items-center justify-between gap-3 pt-5">
                  <span class="text-[10px] text-v2-text-text-muted">
                    {language.t("cowork.enterprise.agents.workspaceOwned")}
                  </span>
                  <button
                    type="button"
                    class="rounded-md bg-v2-background-bg-contrast px-3 py-2 text-[11px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
                    disabled={agents.loading || !!state.adding || isAdded(template.id)}
                    onClick={() => void addTemplate(template)}
                  >
                    {isAdded(template.id)
                      ? language.t("cowork.enterprise.agents.added")
                      : language.t("cowork.enterprise.agents.addToWorkspace")}
                  </button>
                </div>
              </article>
            )}
          </For>
        </div>

        <Show when={state.added}>
          <div class="mt-5 flex justify-end">
            <button type="button" class="text-[12px] font-medium hover:underline" onClick={() => navigate("/agents")}>
              {language.t("cowork.enterprise.agents.viewMyAgents")}
            </button>
          </div>
        </Show>
      </div>
    </div>
  )
}

function ScopeCheckbox(props: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label class="flex items-center gap-2 rounded-md border border-v2-border-border-base px-3 py-2 text-[11px]">
      <input
        type="checkbox"
        checked={props.checked}
        onChange={(event) => props.onChange(event.currentTarget.checked)}
      />
      <span class="truncate">{props.label}</span>
    </label>
  )
}

function toggleID(values: string[], id: string, checked: boolean) {
  return checked ? [...new Set([...values, id])] : values.filter((value) => value !== id)
}

function scopeAssignments(scopeType: ScopeType, entityIDs: string[], projectIDs: string[]) {
  if (scopeType === "workspace") return [{ scope_type: "workspace" as const, scope_id: null }]
  const ids = scopeType === "entity" ? entityIDs : projectIDs
  return ids.map((scopeID) => ({ scope_type: scopeType, scope_id: scopeID }))
}

function scopeFallback(assignment: CoworkAgentAssignment, language: ReturnType<typeof useLanguage>) {
  if (assignment.scope_type === "workspace") return language.t("cowork.enterprise.scope.allCompanies")
  if (assignment.scope_type === "entity") return language.t("cowork.enterprise.agents.selectedEntity")
  return language.t("cowork.enterprise.agents.selectedProject")
}
